import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
// Utils
import { Form, FormElements } from '@zolteam/zol-react-form';
// Components
import { Button, Skeleton } from '@zolteam/zol-admin-ui';
// Context
import { useAppContext } from '../../store/AppContext';
// Services
import { putUpdateAdminUser } from '../../services/parazol/parazolService';
import { useGetAdminRolesListQuery, useGetAdminUserQuery } from '../../services/parazol/parazolQueries';
// Locales
import fr from '../../locales/fr.json';

/**
 * @typedef {object} UserValues
 * @property {string[]} roleIds
 * @property {string} firstName
 * @property {string} lastName
 */

const FORM_NAME = 'user-edit';

const SCHEMA = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'Utilisateur',
  properties: {
    firstName: {
      type: 'string',
      description: 'Prénom',
      minLenght: 1,
      x_layout: '1/1/1/7',
    },
    lastName: {
      type: 'string',
      description: 'Nom',
      minLenght: 1,
      x_layout: '1/7/1/13',
    },
    roleIds: {
      type: 'array',
      description: 'Rôles',
      x_layout: '2/1/2/13',
      minItems: 1,
      items: {
        type: 'string',
        enum: [
          'ADMIN',
        ],
      },
    },
  },
  required: ['firstName', 'lastName', 'roleIds'],
  additionalProperties: false,
};

/**
 * UserProfile component.
 * @returns {JSX.Element}
 */
function UserProfile() {
  // Hooks
  const { t } = useTranslation();
  const { id: userId } = useParams();
  const [, dispatch] = useAppContext();

  // Services
  const { data: adminRolesData } = useGetAdminRolesListQuery({ pageNumber: 1, pageSize: 999 });
  const {
    isLoading: isLoadingUser,
    data: userData,
    refetch: getAdminUserRefetch,
  } = useGetAdminUserQuery(
    userId,
    {
      select: (data) => ({
        ...data,
        roleIds: data.roleIds
          .map((/** @type {string} */userRoleId) => adminRolesData.data
            .find((role) => role.id === userRoleId).name),
      }),
    },
  );
  const updateAdminUserInfoMutation = useMutation({
    mutationFn: (/** @type {UserValues & { version: number }} */values) => putUpdateAdminUser(
      userId,
      values,
    ),
    onSuccess: () => {
      dispatch({
        type: 'SET_MESSAGE',
        payload: { text: t('parazol.userForm.updateSuccess'), type: 'success' },
      });
      getAdminUserRefetch();
    },
  });

  // Handlers
  const onSubmit = (/** @type {UserValues} */values) => {
    /** @type {UserValues & { version: number }} */
    const newUser = {
      ...values,
      roleIds: values.roleIds.map((/** @type {string} */roleName) => adminRolesData.data
        .find((role) => role.name === roleName).id),
      version: userData.version,
    };

    updateAdminUserInfoMutation.mutate(newUser);
  };

  return (
    <>
      <h1 className="h1-default margin-top-m margin-bottom-m" style={{ fontWeight: 'bold' }}>
        {t('parazol.userForm.infoUserTitle')}
      </h1>
      {isLoadingUser || !userData || !adminRolesData
        ? <Skeleton shape="rectangle" height="3.625rem" width="100%" />
        : (
          <Form
            name={FORM_NAME}
            className=""
            callbacks={{
              onSubmit,
            }}
            defaultValues={userData}
            schema={SCHEMA}
            translations={{
              formValidation: fr.cortizol.formValidation,
            }}
          >
            {(formConfig, formInfo) => (
              <FormElements
                formConfig={formConfig}
                formTitle={formInfo.formName}
                translations={{
                  selectOptionLabels: {
                    ADMIN: t('parazol.userNew.roles.admin'),
                    USER: t('parazol.userNew.roles.user'),
                    BLOG: t('parazol.userNew.roles.blog'),
                  },
                }}
              />
            )}
          </Form>
        )}

      <Button
        form={FORM_NAME}
        isSubmit
        isLoading={updateAdminUserInfoMutation.isLoading}
      >
        {t('parazol.userForm.submit')}
      </Button>
    </>
  );
}

export default UserProfile;
